import { PathKind, required, SchemaPath, SchemaPathRules, validate } from '@angular/forms/signals';

/** Options of the {@link requiredText} rule. */
export interface RequiredTextConfig {
    /** message reported when the field carries no text */
    message?: string;
}

/**
 * Whether the text says anything at all: at least one letter or one digit.
 *
 * A field holding only spaces is the case this exists for — the native `required` check reads
 * `' '` as filled in, since it only asks whether the string is empty — but punctuation on its own
 * (`'-'`, `'...'`) names no street and no city either, so the test is for something readable
 * rather than for the absence of whitespace.
 *
 * Matched against the Unicode letter and number categories rather than `A-Z0-9`, so that an
 * address written in any script the technicians actually type — accented Latin, Cyrillic, Greek —
 * counts as text.
 *
 * @param value the text to test, absent counting as no text
 * @returns whether the text carries a letter or a digit
 */
export function hasAlphanumeric(value: string | undefined): boolean {
    return /[\p{L}\p{N}]/u.test(value ?? '');
}

/**
 * Signal-forms schema rule for a mandatory text field: it must be filled in, and what fills it
 * must be text — see {@link hasAlphanumeric}.
 *
 * `required` on its own accepts a field holding only spaces, which is how a mandatory address
 * field could be satisfied by nothing readable. This adds the second half of that question while
 * keeping the first: the field still reports as `required`, so the label's asterisk and the
 * control's `required` attribute are unaffected.
 *
 * An empty field is left to `required` alone to report, so the message is never printed twice.
 *
 * @param path the string path to validate
 * @param config optional message, used for both halves so the field states one expectation
 */
export function requiredText<TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<string, SchemaPathRules.Supported, TPathKind>,
    config?: RequiredTextConfig,
): void {
    required(path, { message: config?.message });

    validate(path, (context) => {
        const value = context.value();
        if (!value || hasAlphanumeric(value)) {
            return undefined;
        }
        return { kind: 'requiredText', message: config?.message ?? '' };
    });
}
